import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS } from '@terminal/react-query-hooks/alfred/useScheduledQueries';
import { queryKeys } from '@terminal/routes/query-params';
import { useNotifications } from '@terminal/stores/use-notifications/useNotifications';
import { QUERY_ALL_CONVERSATIONS } from '@terminal/views/alfred/research/hooks/useFetchInfiniteConversation';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { useCallback } from 'react';

interface Params {
  onSuccess?: () => void;
  onError?: () => void;
}

/**
 * Subscribes to the notifier WebSocket and navigates to the resulting
 * conversation as soon as the first `conversation` event arrives.
 *
 * Returns a `subscribe(scheduledQueryId)` function. Call it right before
 * firing the run mutation and pass the task ID so the correct cache keys
 * are invalidated. It returns an `unsubscribe` function for early cleanup
 * (e.g. on unmount or mutation error).
 */
export const useSubscribeToRunResult = ({
  onSuccess,
  onError,
}: Params = {}) => {
  const subscribeToMessageOfType = useNotifications(
    state => state.subscribeToMessageOfType
  );
  const { navigateToConversation } = useChatNavigation();
  const queryClient = useQueryClient();

  const subscribe = useCallback(
    (scheduledQueryId: string) => {
      const unsubscribe = subscribeToMessageOfType('conversation', message => {
        const { action } = message.event;

        if (action === 'request.fail' || action === 'request.cancel') {
          unsubscribe();
          onError?.();
          return;
        }

        if (action !== 'request.update') {
          return;
        }

        // request.update fires as soon as the conversation is created —
        // navigate immediately so the user sees it processing in real time.
        unsubscribe();
        onSuccess?.();
        navigateToConversation(message.metadata.conversation_id, {
          searchParams: new URLSearchParams({
            [queryKeys.activeHistorySidebarCategory]: 'all',
          }),
        });
        queryClient.invalidateQueries({ queryKey: [QUERY_ALL_CONVERSATIONS] });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS, scheduledQueryId],
        });
      });

      return unsubscribe;
    },
    [
      subscribeToMessageOfType,
      navigateToConversation,
      queryClient,
      onSuccess,
      onError,
    ]
  );

  return { subscribe };
};
